import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton, TextField, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { HttpAuth } from "../../http";
import { userStorage } from "../../utils/storage";
import { UserViewDTO } from "../../types/user/UserView";
import { useTheme } from "../../util/themes";

// react-hook-form + yup
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";

import { ControlledCountryPhoneField } from "../../components/forms/ControlledCountryPhoneField";
import { digitsOnly, validateInternationalPhone, formatInternationalPhoneForSummary } from "@ecommerce/shared-utils";


const PHONE_MAX_DIGITS = 20;
const ALLOWED_COUNTRY_CODES = new Set(["54", "598", "55", "595", "591", "56"]);

type ProfileForm = {
    nombre: string;
    apellido: string;
    codigoArea: string;
    telefono: string;
    fechaNacimiento: string;
};

const schema: yup.ObjectSchema<ProfileForm> = yup.object({
    nombre: yup.string().required("El nombre es obligatorio"),
    apellido: yup.string().required("El apellido es obligatorio"),

    codigoArea: yup
        .string()
        .required("Seleccioná un país.")
        .test("country-code", "Seleccioná un país válido.", (val) => {
            const cc = digitsOnly(String(val ?? ""));
            return !!cc && ALLOWED_COUNTRY_CODES.has(cc);
        }),

    telefono: yup
        .string()
        .required("Ingresá código de área y número.")
        .test("valid-phone", "Ingresá un teléfono válido.", function (val) {
            const phoneRaw = String(val ?? "");
            const parent = this.parent as any;
            const countryRaw = String(parent?.codigoArea ?? "");
            const res = validateInternationalPhone(countryRaw, phoneRaw);
            return res.ok ? true : this.createError({ message: res.message });
        }),

    fechaNacimiento: yup.string().required("La fecha de nacimiento es obligatoria"),
});

const normalizeDate = (date?: string | null) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
};

/* =========================
   Styles
========================= */
const TEXT = {
    pageTitle: {
        ...TYPOGRAPHY_STYLES.rubik,
        fontSize: 24,
        fontWeight: 700,
    },
    label: (theme: any) => ({
        ...TYPOGRAPHY_STYLES.rubikSmall,
        color: theme.colors.text.secondary,
        fontWeight: 700,
        textTransform: "uppercase" as const,
        letterSpacing: "0.3px",
    }),
    value: (theme: any) => ({
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
        color: theme.colors.text.primary,
    }),
} as const;

const ICON_SX = {
    ...BUTTON_STYLES.icon,
    height: 32,
    width: 32,
} as const;

const getFieldSX = (theme: any) => ({
    "& .MuiInputLabel-root": {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        color: theme.colors.text.primary,
    },
    "& .MuiOutlinedInput-root": {
        borderRadius: 0,
        backgroundColor: theme.colors.background.paper,
        color: theme.colors.text.primary,
        "& fieldset": {
            borderColor: theme.colors.greyLight,
        },
        "&:hover fieldset": {
            borderColor: theme.colors.greyLight,
        },
        "&.Mui-focused fieldset": {
            borderColor: theme.colors.greyLight,
        },
    },
    "& .MuiInputBase-input": {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
        color: theme.colors.text.primary,
    },
    "& .MuiFormHelperText-root": {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        marginLeft: 0,
        color: theme.colors.text.secondary,
    },
}) as const;

export const ProfileInfoSection = () => {
    const theme = useTheme();
    const FIELD_SX = getFieldSX(theme);
    
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [profile, setProfile] = useState<UserViewDTO | null>(null);

    const {
        control,
        register,
        handleSubmit,
        setValue,
        getValues,
        trigger,
        reset,
        formState: { errors },
    } = useForm<ProfileForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            nombre: "",
            apellido: "",
            codigoArea: "", // ControlledCountryPhoneField setea AR si está vacio
            telefono: "",
            fechaNacimiento: "",
        },
    });

    const phoneDisplay = useMemo(() => {
        const formatted = formatInternationalPhoneForSummary(
            profile?.codigoArea ?? "",
            profile?.telefono ?? ""
        );

        return formatted || "-";
    }, [profile?.codigoArea, profile?.telefono]);


    useEffect(() => {
        const load = async () => {
            try {
                const data = await HttpAuth.getUser();

                const normalized: UserViewDTO = {
                    ...data,
                    fechaNacimiento: normalizeDate(data.fechaNacimiento),
                };

                setProfile(normalized);

                reset({
                    nombre: data.nombre ?? "",
                    apellido: data.apellido ?? "",
                    codigoArea: data.codigoArea ?? "",
                    telefono: data.telefono ?? "",
                    fechaNacimiento: normalizeDate(data.fechaNacimiento),
                });
            } finally {
                setLoading(false);
            }
        };

        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) return <Typography sx={TEXT.value(theme)}>Cargando...</Typography>;
    if (!profile) return <Typography sx={TEXT.value(theme)}>Error cargando perfil</Typography>;

    const handleCancel = () => {
        reset({
            nombre: profile.nombre ?? "",
            apellido: profile.apellido ?? "",
            codigoArea: profile.codigoArea ?? "",
            telefono: profile.telefono ?? "",
            fechaNacimiento: profile.fechaNacimiento ? normalizeDate(profile.fechaNacimiento) : "",
        });
        setIsEditing(false);
    };

    const onSubmit = async (data: ProfileForm) => {
        const codigoAreaDigits = digitsOnly(data.codigoArea);
        const telefonoDigits = digitsOnly(data.telefono);

        const resp = await HttpAuth.updateProfile({
            nombre: data.nombre,
            apellido: data.apellido,
            codigoArea: codigoAreaDigits,
            telefono: telefonoDigits,
            fechaNacimiento: data.fechaNacimiento,
        });

        userStorage.updateProfile({
            nombre: resp.nombre,
            apellido: resp.apellido,
            apellidoNombre: resp.apellidoNombre,
            codigoArea: resp.codigoArea,
            telefono: resp.telefono,
        });

        const updatedProfile: UserViewDTO = {
            ...resp,
            fechaNacimiento: normalizeDate(resp.fechaNacimiento),
        };

        setProfile(updatedProfile);

        reset({
            nombre: resp.nombre ?? "",
            apellido: resp.apellido ?? "",
            codigoArea: resp.codigoArea ?? "",
            telefono: resp.telefono ?? "",
            fechaNacimiento: normalizeDate(resp.fechaNacimiento),
        });

        setIsEditing(false);
    };

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Typography sx={{ ...TEXT.pageTitle, mb: 3, color: theme.colors.text.primary }}>Información del perfil</Typography>

                {!isEditing && (
                    <IconButton sx={ICON_SX} onClick={() => setIsEditing(true)}>
                        <EditIcon />
                    </IconButton>
                )}
            </Box>

            {!isEditing ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Field label="Nombre" value={profile.nombre ?? "-"} theme={theme} />
                    <Field label="Apellido" value={profile.apellido ?? "-"} theme={theme} />
                    <Field label="Nombre completo" value={profile.apellidoNombre ?? "-"} theme={theme} />

                    <Field label="Teléfono" value={phoneDisplay} theme={theme} />

                    <Field label="Fecha de nacimiento" value={normalizeDate(profile.fechaNacimiento) || "-"} theme={theme} />
                </Box>
            ) : (
                // ==== FORM EDITABLE ====
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                    <TextField
                        label="Nombre"
                        fullWidth
                        {...register("nombre")}
                        error={!!errors.nombre}
                        helperText={errors.nombre?.message}
                        sx={FIELD_SX}
                    />

                    <TextField
                        label="Apellido"
                        fullWidth
                        {...register("apellido")}
                        error={!!errors.apellido}
                        helperText={errors.apellido?.message}
                        sx={FIELD_SX}
                    />

                    <ControlledCountryPhoneField<ProfileForm>
                        control={control}
                        getValues={getValues}
                        setValue={setValue}
                        trigger={trigger}
                        countryCodeName={"codigoArea"}
                        phoneName={"telefono"}
                        required
                        phoneMaxLength={PHONE_MAX_DIGITS}
                        defaultCountryIso2="AR"
                        phoneHelperText={'Ingresá código de área y número. Ej AR celular: " 9 11 1234 5678".'}
                    />

                    <TextField
                        label="Fecha de nacimiento"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        {...register("fechaNacimiento")}
                        error={!!errors.fechaNacimiento}
                        helperText={errors.fechaNacimiento?.message}
                        sx={FIELD_SX}
                    />

                    <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <Button
                            variant="contained"
                            type="submit"
                            sx={{ ...BUTTON_STYLES.invertedPrimary, width: "auto", minWidth: "fit-content" }}
                        >
                            Guardar
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const Field = ({ label, value, theme }: { label: string; value: string; theme: any }) => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" }, gap: 1 }}>
        <Typography sx={TEXT.label(theme)}>{label}</Typography>
        <Typography sx={TEXT.value(theme)}>{value}</Typography>
    </Box>
);

